import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { removeExpiredLoad } from '../store/slices/loadFeedSlice';
import { getServerCorrectedNow } from '../utils/loadUtils';
import { toast } from 'react-toastify';

// Track last server sync timestamp globally to detect when to recalculate timers
let lastKnownSyncTimestamp = 0;

/**
 * Hook that tracks load expiry times and automatically removes expired loads from the feed.
 * 
 * Uses server-synced time (via WebSocket heartbeat) to ensure accurate expiry tracking
 * regardless of client clock drift.
 * 
 * @param {Array} loads - Array of load objects to track
 * @param {Object} options - Configuration options
 * @param {boolean} options.showToast - Whether to show a toast when a load expires (default: true)
 * @param {number} options.bufferMs - Buffer time before expiry to remove (default: 0)
 */
export const useLoadExpiryTracker = (loads, options = {}) => {
  const dispatch = useDispatch();
  const timersRef = useRef({});
  const { showToast = true, bufferMs = 0 } = options;

  const handleLoadExpired = useCallback((load) => {
    // Remove from Redux store
    dispatch(removeExpiredLoad(load.id));
    
    // Show toast notification
    if (showToast) {
      const displayId = load.uuid 
        ? load.uuid.split('-')[0].toUpperCase() 
        : load.id;
      toast.info(`Load #${displayId} has expired and is no longer available for bidding.`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  }, [dispatch, showToast]);

  const scheduleExpiry = useCallback((load) => {
    // Skip if no expiry time
    if (!load.expiresAtUtc) return;
    
    // Skip if already marked as expired by server
    if (load.is_expired === true) {
      // Remove immediately
      handleLoadExpired(load);
      return;
    }

    try {
      const expiresAt = new Date(load.expiresAtUtc);
      if (isNaN(expiresAt.getTime())) return;

      // Use server-corrected time for accurate calculation
      const now = getServerCorrectedNow();
      const timeUntilExpiry = expiresAt.getTime() - now.getTime() - bufferMs;

      // If already expired, remove immediately
      if (timeUntilExpiry <= 0) {
        handleLoadExpired(load);
        return;
      }

      // Clear existing timer for this load if any
      if (timersRef.current[load.id]) {
        clearTimeout(timersRef.current[load.id]);
      }

      // Set up timer to remove when expired
      timersRef.current[load.id] = setTimeout(() => {
        handleLoadExpired(load);
        delete timersRef.current[load.id];
      }, timeUntilExpiry);

    } catch (error) {
      console.error('Error scheduling expiry for load:', load.id, error);
    }
  }, [handleLoadExpired, bufferMs]);

  // Set up timers when loads change
  useEffect(() => {
    if (!loads || !Array.isArray(loads)) return;

    // Get current load IDs
    const currentLoadIds = new Set(loads.map(load => load.id));

    // Clear timers for loads that are no longer in the list
    Object.keys(timersRef.current).forEach(loadId => {
      const numericId = parseInt(loadId, 10);
      if (!currentLoadIds.has(numericId) && !currentLoadIds.has(loadId)) {
        clearTimeout(timersRef.current[loadId]);
        delete timersRef.current[loadId];
      }
    });

    // Schedule expiry for each load
    loads.forEach(load => {
      // Only schedule if status is 'open' (others can't expire in the bidding sense)
      if (load.status?.toLowerCase() === 'open') {
        scheduleExpiry(load);
      }
    });

    // Cleanup function
    return () => {
      // Don't clear timers on every re-render, only on unmount
    };
  }, [loads, scheduleExpiry]);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(timer => {
        clearTimeout(timer);
      });
      timersRef.current = {};
    };
  }, []);

  // Periodically check for server time sync updates and recalculate timers
  // This ensures timers stay accurate after WebSocket heartbeat updates the offset
  useEffect(() => {
    const checkForSyncUpdate = () => {
      const currentSyncTimestamp = window.__lastServerSync || 0;
      if (currentSyncTimestamp > lastKnownSyncTimestamp) {
        lastKnownSyncTimestamp = currentSyncTimestamp;
        // Server time sync was updated - reschedule all timers with new offset
        if (loads && Array.isArray(loads)) {
          loads.forEach(load => {
            if (load.status?.toLowerCase() === 'open') {
              scheduleExpiry(load);
            }
          });
        }
      }
    };

    // Check every 5 seconds for sync updates
    const intervalId = setInterval(checkForSyncUpdate, 5000);
    
    return () => clearInterval(intervalId);
  }, [loads, scheduleExpiry]);

  // Return a function to manually check/refresh expiry
  const checkExpiry = useCallback(() => {
    if (!loads || !Array.isArray(loads)) return;
    
    const now = getServerCorrectedNow();
    loads.forEach(load => {
      if (load.expiresAtUtc && load.status?.toLowerCase() === 'open') {
        const expiresAt = new Date(load.expiresAtUtc);
        if (!isNaN(expiresAt.getTime()) && now >= expiresAt) {
          handleLoadExpired(load);
        }
      }
    });
  }, [loads, handleLoadExpired]);

  return { checkExpiry };
};

export default useLoadExpiryTracker;

